import {Component, DestroyRef, inject, Input, OnDestroy, OnInit} from '@angular/core';
import {FormArray, FormControl, FormGroup} from '@angular/forms';
import {
  debounceTime,
  distinctUntilChanged,
  map,
  Observable, of,
  pairwise,
  shareReplay,
  startWith,
  Subscription, switchMap, take
} from 'rxjs';
import {filter} from 'rxjs/operators';
import {Aangifte, BokkzIbDataService, IncomeTaxEntity, Loon} from '@snelstart-ventures/fiscaal/domain-logic';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {SelectFormControlItem, SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {SharedUiButtonModule} from '@snelstart/ui-button';
import {AsyncPipe} from '@angular/common';
import {SharedUiAlertModule} from '@snelstart/ui-alert';

type LoonFormGroup = FormGroup<{
  loon: OmschrijvingEnBedragFormGroup;
  loonheffing: FormControl<number | null>;
  loonsoort: FormControl<string | null>;
}>;

type OmschrijvingEnBedragFormGroup = FormGroup<{
  omschrijving: FormControl<string | null>;
  bedrag: FormControl<number | null>;
}>;

@Component({
    selector: 'snelstart-venture-fiscaal-feature-loon-vroeger',
    templateUrl: './loon-vroeger.component.html',
    styleUrls: ['./loon-vroeger.component.scss'],
    imports: [SharedUiFormControlsModule, SharedUiButtonModule, AsyncPipe, SharedUiAlertModule],
})
export class LoonVroegerComponent implements OnInit, OnDestroy {
    @Input() public id!: string;
    private dataService = inject(BokkzIbDataService);
    private destroyRef = inject(DestroyRef);
    private changeSubscriptions = new Subscription();
    public dialogVisible = false;
    public index = 0;
    public isPartner = false;
    public isDefAangifte = false;

    readonly incomeTaxEntity$: Observable<IncomeTaxEntity> = this.dataService.incomeTaxEntity$.pipe(
        filter((data): data is IncomeTaxEntity => !!data?.sstRelationId),
        takeUntilDestroyed(this.destroyRef),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    public loonVroegerFg = new FormGroup({
        loonVroegereArbeid: new FormArray<LoonFormGroup>([]),
    });

    public rechtOpAowAlleenstaandeFg = new FormGroup({
        rechtOpAowAlleenstaande: new FormControl<boolean | null>(false),
    });

    public wajongFg = new FormGroup({
        krijgtWajong: new FormControl<boolean | null>(false),
    });

    public optionsLoonsoort: SelectFormControlItem<string>[] = [
        { value: '11', label: 'AOW', disabled: false },
        { value: '17', label: 'Pensioen' },
        { value: '18', label: 'Lijfrente' },
        { value: '12', label: 'WW' },
        { value: '13', label: 'WIA/WAO' },
        { value: '14', label: 'WAJONG' },
        { value: '15', label: 'Ontslag- of transitievergoeding' },
        { value: '16', label: 'Levensloopuitkering' },
        { value: '19', label: 'Bijstand' },
        { value: '20', label: 'Andere uitkeringen' },
    ];

    public optionsJaNee: SelectFormControlItem<boolean | undefined>[] = [
        { value: true, label: 'Ja' },
        { value: false, label: 'Nee' },
    ];

    public optionsJaNeeWajong: SelectFormControlItem<boolean | undefined>[] = [
        { value: false, label: 'Nee, geen wajong-korting' },
        { value: true, label: 'Ja, wel wajong-korting' },
    ];

    ngOnInit(): void {
        this.dataService.partnerAangifte$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((partner) => {
            this.isPartner = partner;
            this.incomeTaxEntity$.pipe(take(1), takeUntilDestroyed(this.destroyRef)).subscribe((entity) => {
                this.isDefAangifte = entity.taxType === 'DA';
                if (partner) {
                    this.handleSwitch(entity, true);
                } else {
                    this.handleSwitch(entity, false);
                }
            });
        });
        this.subscribeToChanges();
    }

    ngOnDestroy(): void {
        this.changeSubscriptions.unsubscribe();
    }

    private handleSwitch(entity: IncomeTaxEntity, isPartner: boolean): void {
        const details = isPartner ? entity.aangifte?.partnerAangifte : entity.aangifte;
        if (details) {
            this.resetAndPatchForms(details);
        }
    }

    private resetAndPatchForms(details: any): void {
        this.loonVroegerFg.reset({}, { emitEvent: false }); // Prevent reset from emitting events
        this.rechtOpAowAlleenstaandeFg.reset({}, { emitEvent: false }); // Prevent reset from emitting events
        this.wajongFg.reset({}, { emitEvent: false }); // Prevent reset from emitting events
        this.updateForm(details.box1?.belastbaarLoon?.loonUitVroegereDienstbetrekking || []);
        this.wajongFg.patchValue({ krijgtWajong: details.heffingskortingen?.krijgtWajong ?? false }, { emitEvent: false });
        this.rechtOpAowAlleenstaandeFg.patchValue(
            { rechtOpAowAlleenstaande: details.heffingskortingen?.rechtOpAowAlleenstaande ?? false },
            { emitEvent: false },
        );
    }

    private subscribeToChanges(): void {
        this.changeSubscriptions.unsubscribe();
        this.changeSubscriptions = new Subscription();

        const extractInputForCalculation = () =>
            this.loonVroegereArbeid.getRawValue().map((item) => ({
                bedrag: item.loon.bedrag,
                loonheffing: item.loonheffing,
            }));

        this.changeSubscriptions.add(
            this.loonVroegereArbeid.valueChanges
                .pipe(
                    debounceTime(500),
                    map(() => this.saveForm()),
                    map(() => extractInputForCalculation()),
                    startWith(extractInputForCalculation()),
                    pairwise(),
                    filter(([prev, curr]) => JSON.stringify(prev) !== JSON.stringify(curr)),
                    switchMap(() => (this.isDefAangifte ? of({} as Aangifte) : this.dataService.calculateLoonheffingen())),
                    switchMap(() => this.dataService.calculateLatestIncomeTaxReturn()),
                    takeUntilDestroyed(this.destroyRef),
                )
                .subscribe(),
        );

        this.changeSubscriptions.add(
            this.wajongFg.controls.krijgtWajong.valueChanges
                .pipe(debounceTime(500), distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
                .subscribe(() => {
                    this.saveForm();
                    this.dataService.calculateLatestIncomeTaxReturn().subscribe();
                }),
        );
    }

    get loonVroegereArbeid(): FormArray<LoonFormGroup> {
        return this.loonVroegerFg.controls.loonVroegereArbeid;
    }

    addRij(): void {
        this.loonVroegereArbeid.push(this.createFormGroup(), { emitEvent: false });
    }

    verwijderRij(index: number): void {
        this.loonVroegereArbeid.removeAt(index, { emitEvent: false });
        this.loonVroegerFg.updateValueAndValidity({ emitEvent: false });
        this.loonVroegerFg.markAsDirty({ emitEvent: false });
        this.saveForm();
        this.subscribeToChanges();
    }

    createFormGroup(item?: Loon): FormGroup {
        return new FormGroup({
            loon: new FormGroup({
                omschrijving: new FormControl<string | null>(item?.loon?.omschrijving ?? null),
                bedrag: new FormControl<number | null>(item?.loon?.bedrag ?? null),
            }),
            loonheffing: new FormControl<number | null>(item?.loonheffing ?? null),
            loonsoort: new FormControl<string | null>(item?.loonsoort ?? null),
        });
    }

    private updateForm(loon: Loon[]): void {
        const lijst = loon || [];
        const formArray = this.loonVroegereArbeid;

        while (formArray.length > lijst.length) {
            formArray.removeAt(formArray.length - 1, { emitEvent: false });
        }

        for (let i = 0; i < lijst.length; i++) {
            if (i < formArray.length) {
                const formGroup = formArray.at(i) as FormGroup;
                formGroup.patchValue(lijst[i], { emitEvent: false });
            } else {
                const formGroup = this.createFormGroup(lijst[i]);
                formArray.push(formGroup, { emitEvent: false });
            }
        }
    }

    hasLoonsoort11() {
        return this.loonVroegereArbeid.controls.some((loon) => loon.get('loonsoort')?.value === '11');
    }

    hasLoonsoort14() {
        return this.loonVroegereArbeid.controls.some((loon) => loon.get('loonsoort')?.value === '14');
    }

    saveForm(): void {
        this.loonVroegerFg.updateValueAndValidity({ emitEvent: false });
        this.wajongFg.updateValueAndValidity({ emitEvent: false });
        this.rechtOpAowAlleenstaandeFg.updateValueAndValidity({ emitEvent: false });

        this.dataService.patchIncomeTaxEntity((currentEntity) => {
            if (!currentEntity.aangifte) {
                return currentEntity;
            }

            const updateLoon = (aangifte: Aangifte) => ({
                ...aangifte,
                box1: {
                    ...aangifte.box1,
                    belastbaarLoon: {
                        ...aangifte.box1?.belastbaarLoon,
                        loonUitVroegereDienstbetrekking: this.loonVroegerFg.getRawValue().loonVroegereArbeid,
                    },
                },
                heffingskortingen: {
                    ...aangifte.heffingskortingen,
                    krijgtWajong: this.wajongFg.getRawValue().krijgtWajong,
                    rechtOpAowAlleenstaande: this.rechtOpAowAlleenstaandeFg.getRawValue().rechtOpAowAlleenstaande,
                },
            });

            const updatedAangifte = this.isPartner
                ? {
                      ...currentEntity.aangifte,
                      partnerAangifte: updateLoon(currentEntity.aangifte.partnerAangifte!),
                  }
                : updateLoon(currentEntity.aangifte);

            return {
                ...currentEntity,
                aangifte: updatedAangifte,
            } as IncomeTaxEntity;
        });
    }

    omschrijvingTeVeelKarakters(index: number): boolean {
        return this.loonVroegereArbeid.at(index).get('loon.omschrijving')?.hasError('maxlength') ?? false;
    }
}
